import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const ProviderLogin = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const sendOtp = async () => {
    try {
      setLoading(true);

      const { data: provider } = await supabase
        .from("providers")
        .select("*")
        .eq("email", email)
        .eq("is_active", true)
        .single();

      if (!provider) {
        alert("Provider account not found");
        return;
      }

      const { error } = await supabase.auth.signInWithOtp({
        email
      });

      if (error) throw error;

      setOtpSent(true);
      alert("OTP sent successfully");
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyProviderOtp = async () => {
  try {
    setLoading(true);

    const { error } = await supabase.auth.verifyOtp({
      email,
      token: otp,
      type: "email",
    });

    if (error) throw error;

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError) throw userError;
    if (!user) throw new Error("Authentication failed");

    const {
      data: provider,
      error: providerError,
    } = await supabase
      .from("providers")
      .select("*")
      .eq("email", user.email)
      .eq("is_active", true)
      .single();

    if (providerError || !provider) {
      await supabase.auth.signOut();
      throw new Error("Provider account not found");
    }

    const { error: updateError } = await supabase
      .from("providers")
      .update({
        user_id: user.id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", provider.id);

    if (updateError) throw updateError;

    navigate("/provider-dashboard");

  } catch (err) {
    console.error(err);
    alert(err.message);
  } finally {
    setLoading(false);
  }
};

  return (
  <div className="min-h-screen flex bg-gradient-to-br from-indigo-900 via-blue-800 to-cyan-700">
    
    {/* Left Side */}
    <div className="hidden lg:flex w-1/2 flex-col justify-center px-16 text-white">
      <div className="max-w-lg">
        <h1 className="text-5xl font-bold leading-tight mb-6">
          NRI Local Buddy
          <span className="block text-cyan-300">
            Provider Portal
          </span>
        </h1>

        <p className="text-lg text-blue-100 mb-8">
          Manage service requests, communicate with customers,
          track payments, and grow your business from a single dashboard.
        </p>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
            <span>Secure OTP Authentication</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
            <span>Real-time Service Requests</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
            <span>Provider Performance Dashboard</span>
          </div>
        </div>
      </div>
    </div>

    {/* Login Card */}
    <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">

        <div className="backdrop-blur-xl bg-white/95 shadow-2xl rounded-3xl p-6 sm:p-8 border border-white/30">

          <div className="text-center mb-8">
           <div className="flex items-center justify-center space-x-2 mb-4">
            

            <img
              src="/images/NRI_Logo.svg"
              alt="NRI Local Buddy Logo"
              className="h-14 md:h-16 w-auto object-contain transition-all duration-300"
            />
          </div>

            <h2 className="text-3xl font-bold text-slate-800">
              Provider Login
            </h2>

            <p className="text-slate-500 mt-2">
              Access your provider dashboard securely
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                !otpSent
                  ? "bg-indigo-600 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              1
            </div>

            <div className="w-16 h-1 bg-slate-200"></div>

            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                otpSent
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              2
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Provider Email
              </label>

              <input
                type="email"
                placeholder="provider@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={otpSent}
                className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              />
            </div>

            {!otpSent ? (
              <button
                onClick={sendOtp}
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Verification Code
                  </label>

                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
                  />
                </div>

                <button
                  onClick={verifyProviderOtp}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {loading
                    ? "Verifying..."
                    : "Verify & Continue"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp("");
                  }}
                  className="w-full text-indigo-600 text-sm hover:underline"
                >
                  Change Email Address
                </button>
              </>
            )}
          </div>

          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-xs text-slate-500">
              Protected by secure OTP authentication
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>
);
};

export default ProviderLogin;